import { useEffect } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useSettingsSearch } from '@frontend/settings-routing';
import { SettingsSection } from '@frontend/settings-ui';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { AvailabilitySettingsTrackingIds } from '../../trackingIds/availability-settings-tracking';
import { AvailabilityRules } from './availability-rules';
import { RuleExceptions } from './rule-exceptions';

enum AvailabilityTabs {
  Rules = 'availability-rules',
  Exceptions = 'rule-exceptions',
}

type Props = {
  locationId: string;
};

export const AvailabilityPage = ({ locationId }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const search = useSettingsSearch() as { tab?: string };
  const initialTab = search.tab === AvailabilityTabs.Exceptions ? AvailabilityTabs.Exceptions : AvailabilityTabs.Rules;

  const handleTabChange = (tabId: string) => {
    let tabName = '';

    if (tabId === AvailabilityTabs.Rules) {
      tabName = 'availability';
    } else if (tabId === AvailabilityTabs.Exceptions) {
      tabName = 'exceptions';
    }

    SchedulerV3.trackPendoEvent(AvailabilitySettingsTrackingIds.events.availabilityTabSelected, {
      tabName,
      locationId,
    });
  };

  // Track Pendo event on page view
  useEffect(() => {
    if (locationId) {
      SchedulerV3.trackPendoEvent(AvailabilitySettingsTrackingIds.events.availabilityPageViewed, {
        locationId,
      });
    }
  }, [locationId]);

  return (
    <SettingsSection css={{ height: '100%' }}>
      <Tabs initialTab={initialTab} onChange={handleTabChange}>
        <Tabs.Bar>
          <Tabs.Tab id={AvailabilityTabs.Rules}>{t('Availability Rules')}</Tabs.Tab>
          <Tabs.Tab id={AvailabilityTabs.Exceptions}>{t('Rule Exceptions')}</Tabs.Tab>
        </Tabs.Bar>
        <Tabs.Panel controller={AvailabilityTabs.Rules} css={{ marginTop: theme.spacing(2) }}>
          <AvailabilityRules locationId={locationId} />
        </Tabs.Panel>
        <Tabs.Panel controller={AvailabilityTabs.Exceptions} css={{ marginTop: theme.spacing(2) }}>
          <RuleExceptions locationId={locationId} />
        </Tabs.Panel>
      </Tabs>
    </SettingsSection>
  );
};
